import { Currency } from '@/modules/common';
import { QuickBooksWarning } from '@/modules/finance/common';
import { hasQuickBooksIntegration } from '@/modules/finance/common/utils/generate-entities.utils';
import { ShortTeamMemberResponse } from '@/modules/team/types/dto/team-member-short.dto';

import { GenerateBillListItem } from '../types';
import { QuickBooksMemberWarningTypeDTO } from '../types/dto/response/quick-books-member-warning.response';

/**
 * A member added by hand never went through the generate endpoint, so it carries no server
 * verdict. The missing QuickBooks link is the one warning derivable client-side, and reproducing
 * it keeps an unlinked member out of the selection exactly as a server-loaded one would be.
 */
const buildMemberQuickBooksWarnings = (
  member: ShortTeamMemberResponse
): QuickBooksWarning<QuickBooksMemberWarningTypeDTO>[] =>
  hasQuickBooksIntegration(member.connectedIntegrations)
    ? []
    : [{ type: QuickBooksMemberWarningTypeDTO.HAS_NO_QUICK_BOOKS_INTEGRATION }];

/**
 * Builds the blank bill row for a member the user added from the "add custom member" picker.
 *
 * Every billable figure starts at zero: the member has no tracked work in the period (that is
 * why they are absent from the generated list), so the row exists purely to carry the custom
 * lines the user is about to add to it.
 */
export const mapShortTeamMemberToCustomBillRow = (
  member: ShortTeamMemberResponse,
  currencyCode: string
): GenerateBillListItem => ({
  id: String(member.id),
  hours: 0,
  effectiveRate: 0,
  currency: {
    code: currencyCode,
    displayName: '',
  } as Currency,
  total: 0,
  member: {
    id: member.id,
    name: member.name,
    email: member.email,
    avatarUrl: member.profileImageUrl,
  },
  currencyCode,
  warnings: buildMemberQuickBooksWarnings(member),
  memberProjects: [],
  memberAddedProjects: [],
  memberTimeOffs: [],
  memberOverheads: [],
  memberSubRows: [],
  canSelectMonthlySalary: false,
  monthlySalaryAmount: 0,
  isMonthlySalaryEnabled: false,
  memberSalaryAdjustment: null,
});
